import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card/Card';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { TickInCircleFilled } from '@swyftx/aviary/icons/filled';
import { Clock24Hours } from '@swyftx/aviary/icons/outlined';

import { useForm } from 'react-hook-form';
import { NavigationURLs } from 'src/lib/navigation/types';

import { LoansStepContainer } from './LoansStepContainer';
import { useLoanApplicationSelector, useLoanApplicationService } from '../../xstate/LoanApplication.context';

function getNextBusinessDayLabel(): string {
  const today = new Date();
  let nextDay = new Date(today);
  nextDay.setDate(today.getDate() + 1);

  // 0 = Sunday, 6 = Saturday
  if (today.getDay() === 5) {
    // Friday
    nextDay.setDate(today.getDate() + 3); // Monday
  } else if (today.getDay() === 6) {
    // Saturday
    nextDay.setDate(today.getDate() + 2); // Monday
  }

  // Format as "Monday", "Tuesday", etc.
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `${dayNames[nextDay.getDay()]}, 2pm AEST`;
}

const SubmittedStep: React.FC = () => {
  const loanApplicationService = useLoanApplicationService();
  const applicationData = useLoanApplicationSelector((state) => state.context.applicationData);

  const navigate = useNavigate();

  const {
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
  });

  const onSubmit = (data: any) => {
    loanApplicationService.send({ type: 'NEXT', data });
  };

  const onDashboardClick = () => {
    navigate(NavigationURLs.LoansDashboard);
  };

  return (
    <LoansStepContainer onSubmit={handleSubmit(onSubmit)} submitDisabled={!isValid} hideTitle>
      <main className='grid gap-32 p-32'>
        <div className='flex flex-col items-center gap-16'>
          <TickInCircleFilled className='h-48 w-48 text-color-text-success' />
          <Heading size='h3' weight='normal'>
            Loan application submitted
          </Heading>
          <Body>Your loan application has been received. Learn what&apos;s next.</Body>
        </div>
        <div className='w-full space-y-16'>
          <Card className='bg-color-background-surface-tertiary p-24'>
            <div className='flex items-start gap-12 p-4'>
              <div className='flex flex-1 flex-col gap-16'>
                <div className='flex flex-col gap-8'>
                  <Heading size='h5'>Wait for bank account verification</Heading>
                  <p className='text-sm text-gray-600'>
                    If you made the $1 deposit during your application, you&apos;ll need to wait until we receive it to
                    verify your bank account.
                  </p>
                </div>
                <div className='flex items-center gap-16 text-color-text-accent'>
                  <Clock24Hours className='h-24 w-24' />
                  <span className='text-sm font-medium'>This may take up to 24 hours</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className='bg-color-background-surface-tertiary p-24'>
            <div className='flex items-start gap-12 p-4'>
              <div className='flex flex-1 flex-col gap-16'>
                <div className='flex flex-col gap-8'>
                  <Heading size='h5'>Wait for loan pre-approval</Heading>
                  <p className='text-sm text-gray-600'>
                    Our team will manually review your loan application within 24 business hours.
                  </p>
                </div>
                <div className='flex items-center gap-16 text-color-text-accent'>
                  <Clock24Hours className='h-24 w-24' />
                  {/* TODO: business days */}
                  <span className='text-sm font-medium'>Current est. approval time: {getNextBusinessDayLabel()}</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className='bg-color-background-surface-tertiary p-24'>
            <div className='flex items-start gap-12 p-4'>
              <div className='flex flex-1 flex-col gap-16'>
                <div className='flex flex-col gap-8'>
                  <Heading size='h5'>Provide security</Heading>
                  <Body className='text-sm'>
                    You&apos;ll need to transfer{' '}
                    <span className='font-semibold text-color-text-accent'>
                      ~{applicationData.securityAmount?.toString()} BTC
                    </span>{' '}
                    before we can finalise your loan.
                  </Body>
                </div>
                <Button size='md' className='w-min'>
                  Transfer security
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <Button variant='outlined' onClick={onDashboardClick}>
          View your Loans
        </Button>
      </main>
    </LoansStepContainer>
  );
};

export { SubmittedStep };
